import { ShieldCheck, Mail, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '@/components/Footer';

const DataProtection = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <Link
            to="/"
            className="text-2xl font-bold text-capledger-teal hover:opacity-80 transition-opacity"
          >
            CapLedger
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="space-y-8">
          {/* Title Section */}
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-foreground">Data Protection Statement</h1>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div>
                <strong>Effective Date:</strong> October 2025
              </div>
              <div>
                <strong>Entity:</strong> Spesvest (Pty) Ltd
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              <strong>Product:</strong> CapLedger
            </div>
            <div className="text-sm text-muted-foreground">
              <strong>Address:</strong> Unit C, Doornbosch Centre, Strand Rd, Stellenbosch, South
              Africa, 7600
            </div>
            <div className="text-sm text-muted-foreground">
              <strong>Email:</strong>{' '}
              <a
                href="mailto:privacy@capledger.co.za"
                className="text-capledger-teal hover:underline"
              >
                privacy@capledger.co.za
              </a>
            </div>
          </div>

          {/* Policy Content */}
          <div className="prose prose-slate max-w-none space-y-8">
            {/* Section 1 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">1. Our Commitment</h2>
              <p className="text-muted-foreground leading-relaxed">
                At Spesvest (Pty) Ltd, we take data protection seriously. CapLedger is designed to
                meet international privacy standards including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>POPIA (Protection of Personal Information Act, South Africa)</li>
                <li>GDPR (General Data Protection Regulation, EU/UK)</li>
                <li>Other equivalent data protection laws applicable to our users globally</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed font-semibold">
                We apply the same level of care and protection to all customer data, regardless of
                location.
              </p>
            </section>

            {/* Section 2 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                2. Roles and Responsibilities
              </h2>
              <div className="space-y-3">
                <p className="text-muted-foreground leading-relaxed">
                  <strong>You (the Customer)</strong> act as the <strong>Data Controller</strong> —
                  you decide what personal data is entered into CapLedger and how it is used.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  <strong>Spesvest (Pty) Ltd</strong>, as the provider of CapLedger, acts as the{' '}
                  <strong>Data Processor</strong> — we process your data strictly under your
                  instructions and for the purposes of providing the service.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                3. Principles of Processing
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We adhere to the following principles:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  <strong>Lawfulness, fairness, and transparency</strong> — data is processed for
                  legitimate business purposes only
                </li>
                <li>
                  <strong>Purpose limitation</strong> — used solely to operate and improve CapLedger
                </li>
                <li>
                  <strong>Data minimisation</strong> — we collect only what is necessary
                </li>
                <li>
                  <strong>Accuracy</strong> — we allow corrections and updates
                </li>
                <li>
                  <strong>Storage limitation</strong> — we retain data only as long as necessary
                </li>
                <li>
                  <strong>Integrity and confidentiality</strong> — data is protected by encryption,
                  access controls, and audit logging
                </li>
              </ul>
            </section>

            {/* Section 4 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">4. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                We use industry-standard security controls, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>TLS 1.3 encryption for data in transit</li>
                <li>AES-256 encryption for data at rest</li>
                <li>Role-based access control</li>
                <li>Immutable audit trails for system actions</li>
                <li>Continuous monitoring and backup redundancy</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                All data is hosted in secure regional data centres within South Africa and/or the
                EU.
              </p>
            </section>

            {/* Section 5 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">5. Data Transfers</h2>
              <p className="text-muted-foreground leading-relaxed">
                When data is transferred across borders (for example, between South African and EU
                servers), we ensure adequate safeguards under:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>GDPR Standard Contractual Clauses, and/or</li>
                <li>POPIA Section 72 requirements for transborder data flow</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">6. Your Rights</h2>
              <p className="text-muted-foreground leading-relaxed">
                Depending on your jurisdiction, you have rights to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>Access your personal data</li>
                <li>Correct or delete information</li>
                <li>Object to or restrict processing</li>
                <li>Request a portable copy of your data</li>
                <li>Withdraw consent where applicable</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                Requests can be made to{' '}
                <a
                  href="mailto:privacy@capledger.co.za"
                  className="text-capledger-teal hover:underline"
                >
                  privacy@capledger.co.za
                </a>
                .
              </p>
            </section>

            {/* Section 7 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">7. Data Breach Response</h2>
              <p className="text-muted-foreground leading-relaxed">
                If a personal data breach occurs, we will:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>Contain and investigate the incident immediately</li>
                <li>Notify affected users without undue delay</li>
                <li>Report to relevant supervisory authorities where legally required</li>
                <li>Maintain a record of all incidents and corrective actions taken</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">8. Subprocessors</h2>
              <p className="text-muted-foreground leading-relaxed">
                Spesvest uses carefully selected subprocessors to operate CapLedger (e.g., hosting,
                email delivery, analytics). All subprocessors are bound by written agreements that
                ensure data protection standards equal to or greater than those under POPIA and
                GDPR. A current list is available upon request.
              </p>
            </section>

            {/* Section 9 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                9. Data Processing Agreement (DPA)
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Enterprise clients or organisations requiring a formal DPA under GDPR Article 28 or
                POPIA Section 20 can request one by emailing{' '}
                <a
                  href="mailto:compliance@capledger.co.za"
                  className="text-capledger-teal hover:underline"
                >
                  compliance@capledger.co.za
                </a>
                .
              </p>
            </section>

            {/* Section 10 - Contact */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">10. Contact and Complaints</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have concerns about data protection:
              </p>
              <div className="bg-muted p-6 rounded-lg space-y-3">
                <div>
                  <p className="text-sm font-semibold text-foreground mb-2">Contact Us:</p>
                  <a
                    href="mailto:privacy@capledger.co.za"
                    className="flex items-center gap-2 text-capledger-teal hover:underline mb-2"
                  >
                    <Mail className="h-4 w-4" />
                    privacy@capledger.co.za
                  </a>
                  <p className="text-sm text-muted-foreground">
                    Postal: Spesvest (Pty) Ltd, Unit C, Doornbosch Centre, Strand Rd, Stellenbosch,
                    South Africa, 7600
                  </p>
                </div>
              </div>

              <div className="bg-muted/50 border-l-4 border-capledger-teal p-4 rounded">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong>If you remain dissatisfied</strong>, you may contact:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4 mt-2">
                  <li>
                    <strong>Information Regulator (South Africa)</strong> —{' '}
                    <a
                      href="mailto:inforeg@justice.gov.za"
                      className="text-capledger-teal hover:underline"
                    >
                      inforeg@justice.gov.za
                    </a>
                  </li>
                  <li>Your local Data Protection Authority (for EU/UK users)</li>
                </ul>
              </div>
            </section>
          </div>

          {/* Trust Badges */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-6 bg-muted/50 rounded-lg border border-border">
              <ShieldCheck className="h-6 w-6 text-capledger-teal flex-shrink-0" />
              <span className="text-sm text-muted-foreground">POPIA & GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-3 p-6 bg-muted/50 rounded-lg border border-border">
              <AlertCircle className="h-6 w-6 text-capledger-teal flex-shrink-0" />
              <span className="text-sm text-muted-foreground">
                Transparent & Accountable Processing
              </span>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DataProtection;
